import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { Message } from '../../services/chatService';
import { exportService } from '../../services/exportService';

interface ExportMenuProps {
  messages: Message[];
  sessionName?: string;
}

const ExportMenu: React.FC<ExportMenuProps> = ({
  messages,
  sessionName = 'chat',
}) => {
  const toast = useToast();

  const handleExportText = () => {
    try {
      exportService.exportToText(messages, sessionName);
      toast({
        title: 'Exported to text',
        description: 'Chat history saved as text file',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description:
          error instanceof Error ? error.message : 'Failed to export chat',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleExportPDF = () => {
    try {
      exportService.exportToPDF(messages, sessionName);
      toast({
        title: 'Exported to PDF',
        description: 'Chat history saved as PDF file',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description:
          error instanceof Error ? error.message : 'Failed to export chat',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const handleCopyAll = async () => {
    try {
      await exportService.copyToClipboard(messages);
      toast({
        title: 'Copied to clipboard',
        description: 'All messages copied',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy messages',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  return (
    <Menu>
      <Tooltip label='Export chat' placement='left'>
        <MenuButton
          as={IconButton}
          aria-label='Export chat'
          icon={<DownloadIcon />}
          size='sm'
          variant='ghost'
          color='white'
          _hover={{ bg: 'whiteAlpha.200' }}
          isDisabled={messages.length <= 1}
        />
      </Tooltip>
      <MenuList>
        <MenuItem onClick={handleExportText}>Export as Text (.txt)</MenuItem>
        <MenuItem onClick={handleExportPDF}>Export as PDF (.pdf)</MenuItem>
        <MenuItem onClick={handleCopyAll}>Copy All to Clipboard</MenuItem>
      </MenuList>
    </Menu>
  );
};

export default ExportMenu;
